import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Eye, EyeOff, Trash2, Wallet, Save, Pencil, FileDown } from "lucide-react";
import {
  useDocument,
  fetchDocumentNumber,
  setDocumentNumber,
  categoryLabel,
  daysUntilExpiry,
  DOCUMENT_CATEGORIES,
} from "@/hooks/use-documents";
import { DocumentAttachments } from "@/components/documents/DocumentAttachments";
import { syncDocumentToCalendarEvents } from "@/lib/document-calendar-events";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/system";
import {
  FormInput,
  FormSelect,
  FormTextarea,
  PrimaryButton,
  SecondaryButton,
  Row,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";
import { featureFlag } from "@/lib/feature-flags";
import { downloadDocumentPdf } from "@/lib/document-pdf";

/**
 * BW10 — Document detail. Sensitive `document_number` is decrypted on demand
 * via the `document_get_number` RPC. The page is read-only by default; the
 * user toggles Edit mode to mutate fields.
 */

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

const ELIGIBLE_FOR_PASS = ["travel", "ticket", "membership"];

export function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useDocument(id);
  const [editing, setEditing] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [decrypted, setDecrypted] = useState<string | null>(null);
  const [number, setNumber] = useState<string>("");

  const d = q.data;

  const reveals = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Missing id");
      return await fetchDocumentNumber(id);
    },
    onSuccess: (n) => {
      setDecrypted(n);
      setReveal(true);
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const save = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      if (!id) throw new Error("Missing id");
      const { error } = await (supabase as unknown as {
        from: (t: string) => any;
      })
        .from("document")
        .update(patch as never)
        .eq("id", id);
      if (error) throw error;
      if (number !== "") {
        await setDocumentNumber(id, number || null);
      }
      // BW20.4c: re-fan-out calendar events for travel kinds. wipeFirst
      // clears prior events for this doc so a changed departure_at /
      // checkin_at is reflected without leaving stale rows behind.
      if (d) {
        const merged = { ...(d.metadata ?? {}), ...((patch.metadata as Record<string, unknown>) ?? {}) };
        await syncDocumentToCalendarEvents(
          {
            id,
            user_id: d.user_id,
            kind: (patch.kind as string) ?? d.kind,
            title: (patch.title as string) ?? d.title,
            metadata: merged,
          },
          { wipeFirst: true },
        );
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document", id] });
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["calendar_event"] });
      toast.show("Document saved.");
      setEditing(false);
      setNumber("");
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const del = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Missing id");
      const { error } = await (supabase as unknown as {
        from: (t: string) => any;
      })
        .from("document")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      toast.show("Document deleted.");
      navigate("/documents");
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const wallet = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Missing id");
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) throw new Error("Not signed in");
      const resp = await fetch(`${WORKER_URL}/api/documents/${id}/wallet-pass`, {
        method: "POST",
        headers: { Authorization: `Bearer ${s.access_token}` },
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status}: ${t.slice(0, 200)}`);
      }
      return await resp.json();
    },
    onSuccess: (j: { url?: string; download_filename?: string }) => {
      void qc.invalidateQueries({ queryKey: ["document", id] });
      if (j.url) window.location.href = j.url;
      toast.show("Wallet pass generated. See modal for next steps.");
    },
    onError: (e) => toast.show(formatError(e)),
  });

  if (!d) {
    return (
      <p style={{ color: "var(--fg-3)" }}>
        {q.isLoading ? "Loading…" : "Document not found."}
      </p>
    );
  }

  const days = daysUntilExpiry(d.expires_at);

  return (
    <article>
      <Link
        to="/documents"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          color: "var(--fg-3)",
          fontSize: "var(--type-body-s)",
          textDecoration: "none",
          marginBottom: "var(--space-3)",
        }}
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Documents
      </Link>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: "var(--space-3)",
          marginBottom: "var(--space-6)",
        }}
      >
        <div>
          <p
            style={{
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              margin: 0,
            }}
          >
            {categoryLabel(d.category)} · {d.kind.replace(/_/g, " ")}
          </p>
          <h1
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "var(--type-display-m)",
              margin: "var(--space-2) 0 0",
            }}
          >
            {d.title}
          </h1>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
                setNumber("");
              }}
              style={btnStyle()}
            >
              <Pencil size={14} strokeWidth={1.5} /> Edit
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              downloadDocumentPdf(d).catch((e) => toast.show(formatError(e)));
            }}
            style={btnStyle()}
          >
            <FileDown size={14} strokeWidth={1.5} /> Download as PDF
          </button>
          {featureFlag.apple_wallet && ELIGIBLE_FOR_PASS.includes(d.category) && (
            <button
              type="button"
              onClick={() => wallet.mutate()}
              disabled={wallet.isPending}
              style={btnStyle()}
            >
              <Wallet size={14} strokeWidth={1.5} />
              {wallet.isPending ? "Generating…" : "Apple Wallet pass"}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (confirm("Delete this document? This cannot be undone.")) {
                del.mutate();
              }
            }}
            disabled={del.isPending}
            style={{ ...btnStyle(), color: "var(--brick)" }}
          >
            <Trash2 size={14} strokeWidth={1.5} /> Delete
          </button>
        </div>
      </header>

      {editing ? (
        <EditForm
          initial={d}
          onCancel={() => {
            setEditing(false);
            setNumber("");
          }}
          onSave={(patch) => save.mutate(patch)}
          saving={save.isPending}
          onNumberChange={setNumber}
          number={number}
        />
      ) : (
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {d.issuer && <Field label="Issuer" value={d.issuer} />}
          <Field
            label="Document number"
            value={
              reveal && decrypted
                ? decrypted
                : decrypted === null && reveal
                  ? "—"
                  : "•••••• ••••••"
            }
            action={
              <button
                type="button"
                onClick={() => {
                  if (reveal) setReveal(false);
                  else reveals.mutate();
                }}
                style={btnStyle()}
              >
                {reveal ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
                {reveal ? "Hide" : "Reveal"}
              </button>
            }
          />
          {d.issued_at && <Field label="Issued" value={d.issued_at} />}
          {d.expires_at && (
            <Field
              label="Expires"
              value={`${d.expires_at}${days != null ? ` · T−${days}d` : ""}`}
            />
          )}
          {d.issued_country && <Field label="Country" value={d.issued_country} />}
          {Object.keys(d.metadata ?? {}).length > 0 && (
            <Field
              label="Metadata"
              value={JSON.stringify(d.metadata, null, 2)}
              mono
            />
          )}
          {id ? <DocumentEvents documentId={id} /> : null}
          {id ? <DocumentAttachments documentId={id} /> : null}
          {d.notes && <Field label="Notes" value={d.notes} />}
          {d.wallet_pass_url && (
            <Field
              label="Wallet pass"
              value={`Generated · ${d.wallet_pass_signed ? "signed" : "unsigned"}`}
              action={
                <a href={d.wallet_pass_url} style={btnStyle() as React.CSSProperties}>
                  Download
                </a>
              }
            />
          )}
        </div>
      )}
    </article>
  );
}

function Field({
  label,
  value,
  action,
  mono,
}: {
  label: string;
  value: string;
  action?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        padding: "var(--space-3) var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        display: "grid",
        gridTemplateColumns: "180px 1fr auto",
        gap: "var(--space-3)",
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {label}
      </span>
      <span
        className={mono ? "mono" : undefined}
        style={{
          color: "var(--fg)",
          fontSize: mono ? "var(--type-meta)" : "var(--type-body-s)",
          whiteSpace: mono ? "pre-wrap" : "normal",
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
      <span>{action}</span>
    </div>
  );
}

function EditForm({
  initial,
  onCancel,
  onSave,
  saving,
  number,
  onNumberChange,
}: {
  initial: import("@/hooks/use-documents").DocumentRow;
  onCancel: () => void;
  onSave: (patch: Record<string, unknown>) => void;
  saving: boolean;
  number: string;
  onNumberChange: (v: string) => void;
}) {
  const [title, setTitle] = useState(initial.title);
  const [issuer, setIssuer] = useState(initial.issuer ?? "");
  const [issuedAt, setIssuedAt] = useState(initial.issued_at ?? "");
  const [expiresAt, setExpiresAt] = useState(initial.expires_at ?? "");
  const [country, setCountry] = useState(initial.issued_country ?? "");
  const [notes, setNotes] = useState(initial.notes ?? "");
  const [category, setCategory] = useState(initial.category);
  const [kind, setKind] = useState(initial.kind);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          title,
          issuer: issuer || null,
          issued_at: issuedAt || null,
          expires_at: expiresAt || null,
          issued_country: country || null,
          notes,
          category,
          kind,
        });
      }}
      style={{ display: "grid", gap: "var(--space-3)" }}
    >
      <FormInput label="Title" value={title} onChange={setTitle} />
      <Row>
        <FormSelect
          label="Category"
          value={category}
          onChange={setCategory}
          options={DOCUMENT_CATEGORIES}
        />
        <FormInput label="Kind" value={kind} onChange={setKind} placeholder="passport, flight_ticket…" />
      </Row>
      <FormInput label="Issuer" value={issuer} onChange={setIssuer} />
      <FormInput
        label="Document number (encrypted at rest)"
        value={number}
        onChange={onNumberChange}
        placeholder="Leave empty to keep current"
      />
      <Row>
        <FormInput label="Issued" type="date" value={issuedAt} onChange={setIssuedAt} />
        <FormInput label="Expires" type="date" value={expiresAt} onChange={setExpiresAt} />
      </Row>
      <FormInput label="Country (ISO-2)" value={country} onChange={setCountry} placeholder="DE, US, CH…" />
      <FormTextarea label="Notes" value={notes} onChange={setNotes} rows={3} />
      <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
        <SecondaryButton onClick={onCancel} disabled={saving}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={saving}>
          <Save size={14} strokeWidth={1.5} style={{ marginRight: 6 }} />
          {saving ? "Saving…" : "Save"}
        </PrimaryButton>
      </div>
    </form>
  );
}

function btnStyle(): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 10px",
    background: "transparent",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-chip)",
    color: "var(--fg-2)",
    fontSize: "var(--type-meta)",
    cursor: "pointer",
    textDecoration: "none",
  };
}

function DocumentEvents({ documentId }: { documentId: string }) {
  const q = useQuery({
    queryKey: ["calendar_event", "by-document", documentId],
    enabled: Boolean(documentId),
    queryFn: async (): Promise<Array<{ id: string; starts_at: string; title: string }>> => {
      const sb = supabase as unknown as { from: (t: string) => any };
      const { data, error } = await sb
        .from("calendar_event")
        .select("id, starts_at, title")
        .eq("document_id", documentId)
        .order("starts_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; starts_at: string; title: string }>;
    },
  });
  const events = q.data ?? [];
  if (events.length === 0) return null;
  return (
    <section
      aria-label="Linked calendar events"
      style={{
        padding: "var(--space-3)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div
        style={{
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {events.length} calendar event{events.length === 1 ? "" : "s"}
      </div>
      {events.map((e) => (
        <div key={e.id} style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-2)" }}>
          <span style={{ fontSize: "var(--type-body-s)", color: "var(--fg)" }}>{e.title}</span>
          <span className="mono" style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
            {new Date(e.starts_at).toLocaleString()}
          </span>
        </div>
      ))}
    </section>
  );
}
